import type { QueryClient } from '@tanstack/react-query'
import type { History } from 'history'

import type { EventEmitterWithLogger } from '@surglogs/eventemitter'

import type { FlagReactInstanceType } from '@surglogs-app/feature-flags'
import type { SvgIcon } from '@surglogs-app/icons'
import type {
  AccessManagementAccountGetResponse,
  AccessManagementClinicUserGetResponse,
  ClinicAPIEntity,
} from '@surglogs-app/network'

export interface Config {
  isProd: boolean
  appVersion?: string
  appUrl?: string
  authUrl?: string
  auditsUrl?: string
  dashboardUrl?: string
  assetsPath?: string
  sentryDsn: string
  sentryProjectId: string
  intercomId: string
  posthog: Partial<{ key: string; host: string }>
  trialRequestUrl: string
  trialRequestCalendlyUrl: string
  billingContactEmail: string
  reCaptchaKey: string
  onlyofficeUrl: string
}

export enum AuthTokenStoreEvent {
  TOKEN_UPDATED = 'TOKEN_UPDATED',
}

export interface AuthTokenStoreEventData {
  token: string | null
  refreshToken: string | null
  expiresIn: number | null
  lastRefresh: number | null
}

export interface IAuthTokenStore
  extends EventEmitterWithLogger<AuthTokenStoreEvent> {
  setToken(token: string, refreshToken?: string, expiresIn?: number): this
  clear(): this
  getToken(): string | null
  getRefreshToken(): string | null
  getTokenMetaData(): {
    expiresIn: number | null
    lastRefresh: number | null
  }
  isTokenValid(): boolean
  isRefreshTokenValid(): boolean
  isTokenAboutToExpire(): boolean
  onChange(listener: (data: AuthTokenStoreEventData) => void): () => void
}

export type ClinicContextStoreEventData = ClinicAPIEntity | null

export type AuthContextData = {
  user:
    | AccessManagementClinicUserGetResponse
    | AccessManagementAccountGetResponse
}

export type AuthContextDataRoot = {
  user: AccessManagementClinicUserGetResponse
}

export enum AuthContextStoreEvent {
  UPDATED = 'UPDATED',
}

export enum ClinicContextStoreEvent {
  CLINIC_UPDATED = 'CLINIC_UPDATED',
  CLINIC_ID_UPDATED = 'CLINIC_ID_UPDATED',
}

export interface AuthContextStoreEventData {
  userId: number | null
  sessionId: string | null
}

export interface IAuthContextStore
  extends EventEmitterWithLogger<AuthContextStoreEvent> {
  setUserId(userId: number | string | null): this

  getUserId(): number | null

  getData(): AuthContextStoreEventData

  onChange(listener: (data: AuthContextStoreEventData) => void): () => void

  clearUserId(): this
  getSessionId(): string | null
  setSessionId(sessionId: string): this
}

export interface IClinicContextStore
  extends EventEmitterWithLogger<ClinicContextStoreEvent> {
  getData(): ClinicContextStoreEventData
  onChange(
    listener: (data: ClinicContextStoreEventData | number) => void,
  ): () => void
  changeClinic(clinicId: number, token: string): this
  refetchClinicData(token: string): this
  getClinicId(): number | null
  setClinicId(clinicId: number | null): this
  clear(): this
}

export interface ClinicContextStoreListener<T> {
  (data: T | null): void
}

export enum AuthContextDataStoreEvent {
  UPDATED = 'UPDATED',
  UPDATING = 'UPDATING',
}

export interface AuthDataFetcherParams {
  token: string
}

export interface AuthDataFetcher<TData> {
  (data: AuthDataFetcherParams): Promise<TData>
}

export interface AuthContextDataStoreListener<T> {
  (data: T | null): void
}

export interface IAuthContextDataStore<TData>
  extends EventEmitterWithLogger<AuthContextDataStoreEvent> {
  getData(): TData | null
  on(
    eventName: AuthContextDataStoreEvent,
    fn: AuthContextDataStoreListener<TData | null>,
  ): this
  onChange(listener: AuthContextDataStoreListener<TData | null>): () => void
  onValidChange(listener: (data: TData) => void): () => void
  fetchData(): Promise<TData> | undefined
  isDataFetching(): boolean
  clearData(): this
}

// interface SPAEvent extends Event {
//   path: Window[]
//   detail: {
//     oldUrl: string
//     newUrl: string
//     totalAppChanges: number
//     navigationIsCanceled: boolean
//   }
// }

/**
 * I don't like this solution, in general global things are bad
 * but in order to get rid of circular dependencies in a sane way
 * I needed to offer them in a global manner
 */
declare global {
  interface Auth {
    /** @deprecated access token and handlers using useAuthToken hook instead */
    tokenStore: IAuthTokenStore
    contextStore: IAuthContextStore
    contextDataStore: IAuthContextDataStore<AuthContextData>
  }

  interface Window {
    __DATAFILE__?: Record<string, any>
    // Cypress?: Cypress.Cypress
    Cypress?: any
    Playwright?: boolean
    __analyticsEvents?: Array<{
      name: string
      payload?: Record<string, unknown>
    }>
    // addEventListener(
    //   event: 'single-spa:routing-event',
    //   handler: (e: SPAEvent) => void,
    // ): void
  }

  interface Module {
    id:
      | 'REPORTS'
      | 'DASHBOARD'
      | 'COMPLIANCE_ASSISTANT'
      | 'STANDARDS'
      | 'POLICIES'
      | 'CONTRACTS'
      | 'DOCUMENTS'
      | 'SURGICALLOGS'
      | 'TRACKING'
      | 'PERSONNEL_FILES'
      | 'AUDITS'
      | 'SETTINGS'
      | 'EDUCATION'
      | 'TASKS'
      | 'FILLABLE_FORMS'
      | 'MORE'
      | 'IDEAS'
      | 'HELP'
      | 'EXPORTS'
    icon: SvgIcon
    url: ((clinicId: number) => string) | string
    subUrls?: string[]
    image?: string
  }
}

export interface BootstrapHook {
  (
    config: Config,
    context: {
      history: History
      auth: Auth
      featureFlagsInstance: FlagReactInstanceType
      queryClient: QueryClient
      clinicContextStoreInstance: IClinicContextStore
    },
  ): void
}
